import { API, graphqlOperation } from "aws-amplify";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Loader } from "@aws-amplify/ui-react";

import ServiceOrderForm from "components/serviceOrders/ServiceOrderForm";
import {
  createServiceOrderEquipment,
  createServiceOrderMaintenanceRoutine,
  createServiceOrderTool,
  deleteServiceOrderEquipment,
  deleteServiceOrderMaintenanceRoutine,
  deleteServiceOrderTool,
  updateServiceOrder,
  updateSpare,
} from "graphql/mutations";
import {
  getServiceOrder,
  listEquipment,
  listServiceOrderEquipments,
  listServiceOrderMaintenanceRoutines,
  listServiceOrderTools,
} from "graphql/queries";
import { serviceOrderStatus } from "config/constants";
import LoaderFullScreen from "components/utils/LoaderFullScreen";

const ServiceOrderUpdateForm = ({ serviceOrderId, onSuccess }) => {
  const navigate = useNavigate();
  const [serviceOrder, setServiceOrder] = useState();
  const [loading, setLoading] = useState(false);

  async function onSubmit(valuesParam) {
    try {
      const {
        description,
        billDate,
        billValue,
        billCurrency,
        companyID,
        branchID,
        costCenterID,
        color,
        observations,
        title,
        userExecutor,
        start,
        end,
        tools,
        spares,
        maintenanceRoutines,
        auxExecutors,
        geolocation,
        equipments,
        equipmentRoutines,
        equipmentSpares,
        photoRequired,
      } = valuesParam;

      setLoading(true);

      const promises = [];

      // Update tools
      const toolsToUnLink = serviceOrder.tools?.filter(
        (servTool) => !tools?.some((tool) => tool === servTool)
      );
      toolsToUnLink?.forEach(async (toolId) => {
        const records = (
          await API.graphql({
            query: listServiceOrderTools.replaceAll("__typename", ""),
            variables: {
              filter: {
                and: [
                  { toolId: { eq: toolId } },
                  { serviceOrderId: { eq: serviceOrderId } },
                ],
              },
            },
          })
        )?.data?.listServiceOrderTools?.items;
        promises.push(
          API.graphql({
            query: deleteServiceOrderTool.replaceAll("__typename", ""),
            variables: {
              input: {
                id: records[0].id,
              },
            },
          })
        );
      });
      const toolsToLink = tools?.filter(
        (tool) => !serviceOrder.tools?.some((rec) => rec === tool)
      );
      toolsToLink?.forEach((toolId) => {
        promises.push(
          API.graphql({
            query: createServiceOrderTool.replaceAll("__typename", ""),
            variables: {
              input: {
                toolId,
                serviceOrderId,
              },
            },
          })
        );
      });

      // Update spares
      const sparesToUnLink = serviceOrder.spares?.filter(
        (servSpare) => !spares?.some((spare) => spare === servSpare)
      );
      sparesToUnLink?.forEach(async (spareId) => {
        promises.push(
          API.graphql({
            query: updateSpare.replaceAll("__typename", ""),
            variables: {
              input: {
                id: spareId,
                serviceOrderID: null,
              },
            },
          })
        );
      });
      const sparesToLink = spares?.filter(
        (spare) => !serviceOrder.spares?.some((rec) => rec === spare)
      );
      sparesToLink?.forEach((spareId) => {
        promises.push(
          API.graphql({
            query: updateSpare.replaceAll("__typename", ""),
            variables: {
              input: {
                id: spareId,
                serviceOrderID: serviceOrderId,
              },
            },
          })
        );
      });

      // Update maintenance routines
      const maintenanceRoutinesToUnLink =
        serviceOrder.maintenanceRoutines?.filter(
          (servMaintenanceRoutine) =>
            !maintenanceRoutines?.some(
              (maintenanceRoutine) =>
                maintenanceRoutine === servMaintenanceRoutine
            )
        );
      maintenanceRoutinesToUnLink?.forEach(async (maintenanceRoutineId) => {
        const records = (
          await API.graphql({
            query: listServiceOrderMaintenanceRoutines.replaceAll(
              "__typename",
              ""
            ),
            variables: {
              filter: {
                and: [
                  { maintenanceRoutineId: { eq: maintenanceRoutineId } },
                  { serviceOrderId: { eq: serviceOrderId } },
                ],
              },
            },
          })
        )?.data?.listServiceOrderMaintenanceRoutines?.items;
        promises.push(
          API.graphql({
            query: deleteServiceOrderMaintenanceRoutine.replaceAll(
              "__typename",
              ""
            ),
            variables: {
              input: {
                id: records[0].id,
              },
            },
          })
        );
      });
      const maintenanceRoutinesToLink = maintenanceRoutines?.filter(
        (maintenanceRoutine) =>
          !serviceOrder.maintenanceRoutines?.some(
            (rec) => rec === maintenanceRoutine
          )
      );
      maintenanceRoutinesToLink?.forEach((maintenanceRoutineId) => {
        promises.push(
          API.graphql({
            query: createServiceOrderMaintenanceRoutine.replaceAll(
              "__typename",
              ""
            ),
            variables: {
              input: {
                maintenanceRoutineId,
                serviceOrderId,
              },
            },
          })
        );
      });

      const equipmentsToUnLink = serviceOrder.equipments
        ?.filter(
          (serviceOrderUs) =>
            !equipments?.some((equipment) => equipment.id === serviceOrderUs.id)
        )
        ?.map((equipment) => equipment.id);
      equipmentsToUnLink?.forEach(async (equipmentId) => {
        const serviceOrderEquipmentRecords = (
          await API.graphql({
            query: listServiceOrderEquipments.replaceAll("__typename", ""),
            variables: {
              filter: {
                and: [
                  { equipmentId: { eq: equipmentId } },
                  { serviceOrderId: { eq: serviceOrderId } },
                ],
              },
            },
          })
        )?.data?.listServiceOrderEquipments?.items;
        promises.push(
          API.graphql({
            query: deleteServiceOrderEquipment.replaceAll("__typename", ""),
            variables: {
              input: {
                id: serviceOrderEquipmentRecords[0].id,
              },
            },
          })
        );
      });
      const equipmentsToLink = equipments
        ?.filter(
          (equipment) =>
            !serviceOrder.equipments?.some(
              (serviceOrderEquipment) =>
                serviceOrderEquipment.id === equipment.id
            )
        )
        ?.map((equipment) => equipment.id);
      equipmentsToLink?.forEach((equipmentId) => {
        promises.push(
          API.graphql({
            query: createServiceOrderEquipment.replaceAll("__typename", ""),
            variables: {
              input: {
                serviceOrderId,
                equipmentId,
              },
            },
          })
        );
      });

      await Promise.all(promises);

      const status =
        serviceOrder.status === serviceOrderStatus.created ||
        serviceOrder.status === serviceOrderStatus.scheduled
          ? start && end
            ? serviceOrderStatus.scheduled
            : serviceOrderStatus.created
          : serviceOrder.status;

      await API.graphql({
        query: updateServiceOrder.replaceAll("__typename", ""),
        variables: {
          input: {
            id: serviceOrderId,
            description,
            billDate,
            billValue,
            billCurrency,
            companyID,
            branchID,
            costCenterID,
            color,
            observations,
            title,
            userExecutor: userExecutor || null,
            auxExecutors,
            status,
            start: start || null,
            end: end || null,
            geolocation,
            equipmentRoutines,
            equipmentSpares,
            photoRequired,
          },
        },
      });

      toast.success(strings.successUpdated);
      if (onSuccess) onSuccess();
      else navigate("/service-orders");
    } catch (err) {
      toast.error(strings.responses[err]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchServiceOrder(id) {
    try {
      const serviceOrderData = await API.graphql(
        graphqlOperation(getServiceOrder, { id })
      );
      const serviceOrderItem = serviceOrderData.data.getServiceOrder;
      if (!serviceOrderItem) {
        toast.error(strings.responses.errorFetch);
        navigate("/service-orders");
        return;
      }

      if (serviceOrderItem.equipments?.items?.length > 0) {
        const equipmentsData = (
          await API.graphql(
            graphqlOperation(listEquipment, {
              filter: {
                or: serviceOrderItem.equipments?.items?.map((eq) => ({
                  id: { eq: eq.equipmentId },
                })),
              },
            })
          )
        ).data.listEquipment.items;
        serviceOrderItem.equipments.items =
          equipmentsData?.map((eq) => ({ id: eq.id, name: eq.name })) ?? [];
      }

      setServiceOrder({
        ...serviceOrderItem,
        tools: serviceOrderItem.tools?.items?.map((t) => t.toolId) ?? [],
        spares: serviceOrderItem.spares?.items?.map((t) => t.id) ?? [],
        maintenanceRoutines:
          serviceOrderItem.maintenanceRoutines?.items?.map(
            (t) => t.maintenanceRoutineId
          ) ?? [],
        equipments: serviceOrderItem.equipments?.items ?? [],
        equipmentRoutines:
          serviceOrderItem.equipmentRoutines?.map((eqR) => ({
            equipmentID: eqR.equipmentID,
            routineID: eqR.routineID,
          })) ?? [],
        equipmentSpares:
          serviceOrderItem.equipmentSpares?.map((eqS) => ({
            equipmentID: eqS.equipmentID,
            spares: eqS.spares,
          })) ?? [],
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  useEffect(() => {
    fetchServiceOrder(serviceOrderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceOrderId]);

  if (!serviceOrder) return <Loader />;

  return (
    <div>
      <ServiceOrderForm
        onSubmit={onSubmit}
        type="update"
        initialValues={serviceOrder}
      />
      {loading && <LoaderFullScreen />}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Actualizar Orden de Servicio",
    successUpdated: "Orden de Servicio actualizada con éxito",
    responses: {
      errorFetch: "Error al consultar la orden de servicio",
    },
  },
});

export default ServiceOrderUpdateForm;
